import {
	Button,
	Checkbox,
	CPKSettings,
	ErrorLabel,
	Grid,
	H2,
	H3,
	InputField,
	Label,
	MemoizedNumberField,
	NumberField,
	Radio,
	Spacer,
	TempChip,
	Text,
	Tile,
} from '@components';
import { useRecoilValue } from 'recoil';
import {
	Quote,
	RateCardFormErrors,
	RateCardActiveItemTypeId
} from '@state';
import {
	BUTTON_ICON_POS,
	BUTTON_TYPES,
	ILane,
	ILaneRateCard,
	RATE_CARD_TRUCK_SIZE,
	RateCardType,
	SettingType,
	TEMP_NAMES,
	TFullLoadRangePrices,
	TRateCardRules,
	TItemType,
} from '@types';
import styles from './RateCard.module.scss';
import { getRateCardTruckSizeLabel } from '@utils/truckSizeTypesLabels';
import {
	useClearRCSettingsError, useIsInRoute, useRateCardState, useRateCardRulesState, useRateCardTypesState
} from '@hooks';
import { ChangeEvent, useEffect, useState } from 'react';
import { RateCardModifiers } from './Modifiers';
import { CPIIncreasePanel } from './CPIIncreasePanel';
import { RateCardRules } from './Rules';
import { TrashIcon } from '@img/icons';
import { TruckSizeFloatingMenu } from './TruckSizeFloatingMenu';
import { RangeFloatingMenu } from './RangeFloatingMenu';
import {
	formatItemTypeUnits, getItemRateCardTitle, getRateCardIndex, matchesRateCard, rateCardTypeFromCargoTypeId
} from '@utils';
import { ItemTypeBanner } from '@components/PAGE_LAYOUTS/RC_Settings/ItemTypeBanner';

type TRateCardContentProps = {
	rc: ILaneRateCard;
	settingType: SettingType;
	handleDeleteRateCard: (rcItemIdOrType: RateCardType | string) => void;
	isDisabled?: boolean;
	isLinkedRateCard?: boolean;
	/** On an Item rate card: its Item type, when the live Item types hold it. */
	itemType?: TItemType;
	/** Whether the live Item types have loaded, so an Item type missing from them is a deleted one. */
	itemTypesLoaded?: boolean;
}

const TruckSizeOrderedBySize = [
	'RIGID',
	'SINGLE',
	'B_DOUBLE',
	'B_DOUBLE_HIGH_CUBE',
	'SUPER_B_DOUBLE',
	'B_TRIPLE',
	'A_DOUBLE',
	'CONTAINER_20_FT_COASTAL',
	'CONTAINER_40_FT_COASTAL',
	'CONTAINER_46_FT_COASTAL',
	'CONTAINER_48_FT_COASTAL',
];

export const RateCardContent = (props: TRateCardContentProps) => {
	const {
		rc,
		settingType,
		handleDeleteRateCard,
		isDisabled = false,
		isLinkedRateCard = false,
		itemType,
		itemTypesLoaded = false,
	} = props;
	const quoteInRoute = useIsInRoute('quote');
	const [rateCard, setRateCard] = useRateCardState();

	const rcActiveItemID = useRecoilValue(RateCardActiveItemTypeId);
	const quote = useRecoilValue(Quote);
	const [sortedTruckList, setSortedTruckList] = useState<RATE_CARD_TRUCK_SIZE[] | null>(null);
	const [rateCardTypes] = useRateCardTypesState();
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const clearRCSettingsError = useClearRCSettingsError();
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes: [],
		rateCardTypes,
		itemTypeId: rc.item_type_id,
		isSettings: false,
		type: rc.type,
	});
	const [isEditDisabled, setIsEditDisabled] = useState<boolean>(isDisabled);
	const [rateCardRules, setRateCardRules] = useRateCardRulesState();

	useEffect(() => {
		if (isDisabled || (
			quoteInRoute &&
			quote && (
				rateCardTypeFromCargoTypeId(quote?.quote?.cargo_type_id) !== rc.type ||
				(
					(quote?.quote_shipment?.cargos[0]?.cargo_item?.item_id && rcActiveItemID) &&
					quote?.quote_shipment?.cargos[0]?.cargo_item?.item_id !== rcActiveItemID
				)
			)
		)) {
			setIsEditDisabled(true);
			return;
		}
		setIsEditDisabled(false);
	}, [quoteInRoute, quote, rc, isDisabled])

	// Conditionalled disable RateCardRule setting
	const isRateCardRuleDisabled = () => {
		if (isLinkedRateCard) return false;
		return isEditDisabled;
	}

	const isCPKConfigurationDisabled = () => {
		if (isLinkedRateCard) return false;
		return isEditDisabled;
	}

	const handleInputChange = (rateCardType: RateCardType, temp: string, rangeString: string, value: string ) => {
		let updatedRateCards;
		if (rangeString.includes('margin')) {
			updatedRateCards = rateCard?.rate_cards?.map((rCard) => {
				if (!matchesRateCard(rCard, rateCardType, rc.item_type_id)) return rCard;

				return {
					...rCard,
					margins: {
						...rCard.margins,
						[temp]: parseFloat(Number(value).toFixed(4))
					}
				};
			});
		} else {
			updatedRateCards = rateCard?.rate_cards?.map((rCard) => {
				if (!matchesRateCard(rCard, rateCardType, rc.item_type_id)) return rCard;

				return {
					...rCard,
					rangePrices: {
						...rCard.rangePrices,
						[rangeString]: {
							...rCard.rangePrices[rangeString as keyof typeof rCard.rangePrices],
							[temp]: parseFloat(Number(value).toFixed(4))
						},
					},
				};
			});
		}
		const update = {
			...rateCard,
			rate_cards: updatedRateCards
		}

		// @ts-ignore
		if (rcFormErrors?.rate_card_prices?.[rateCardTypeIndex]?.rangePrices) {
			clearRCSettingsError(`rate_card_prices.${rateCardTypeIndex}.rangePrices`)
		}

		setRateCard(update as ILane);
	}

	const handleAddTruck = (truck: keyof TFullLoadRangePrices) => {
		if (isEditDisabled) return;
		const updatedRateCards = rateCard?.rate_cards?.map((rCard) => {
			if (rCard.type !== 'FULL_LOAD' || rCard.type !== rc?.type) return rCard;
			return {
				...rCard,
				rangePrices: {
					...rCard.rangePrices,
					[truck]: {}
				}
			};
		});
		const update = {
			...rateCard,
			rate_cards: updatedRateCards
		}
		setRateCard(update as ILane);
		// @ts-ignore
		if (rcFormErrors?.rate_card_prices?.[rateCardTypeIndex]?.rangePrices) {
			clearRCSettingsError(`rate_card_prices.${rateCardTypeIndex}.rangePrices`)
		}
	}

	const handleEditTruck = (
		prevTruck: keyof TFullLoadRangePrices,
		newTruck: keyof TFullLoadRangePrices
	) => {
		if (isEditDisabled) return;
		const updatedRateCards = rateCard?.rate_cards?.map((rCard) => {
			if (rCard.type !== 'FULL_LOAD' || rCard.type !== rc.type) return rCard;
			// Maintain existing order
			const updatedObj = Object.fromEntries(
				Object.entries(rCard.rangePrices).map(([key, value]) =>
					key === prevTruck ? [newTruck, value] : [key, value]
				)
			);

			return {
				...rCard,
				rangePrices: updatedObj
			};
		});
		const update = {
			...rateCard,
			rate_cards: updatedRateCards
		}

		setRateCard(update as ILane);
		// @ts-ignore
		if (rcFormErrors?.rate_card_prices?.[rateCardTypeIndex]?.rangePrices) {
			clearRCSettingsError(`rate_card_prices.${rateCardTypeIndex}.rangePrices`)
		}
	}

	const handleDeleteTruck = (truck: keyof TFullLoadRangePrices | null) => {
		if (!truck || isEditDisabled) return;
		const updatedRateCards = rateCard?.rate_cards?.map((rCard) => {
			if (rCard.type !== 'FULL_LOAD') return rCard;
			const { [truck]: truckToRemove, ...newRangePrices } = rCard.rangePrices;
			return {
				...rCard,
				rangePrices: newRangePrices
			}
		});
		const update = {
			...rateCard,
			rate_cards: updatedRateCards
		}

		setRateCard(update as ILane);

	}

	const handleAddRange = (newRange: {
		start: string;
		end: string;
	}, index: number) => {
		if (isEditDisabled) return;
		const updatedRateCards = rateCard?.rate_cards?.map((rCard) => {
			if (rCard.type === 'FULL_LOAD') return rCard;
			if (!matchesRateCard(rCard, rc.type, rc.item_type_id)) return rCard;
			const entries = Object.entries(rCard.rangePrices);
			entries.splice(index + 1, 0, [
				`${newRange.start}-${newRange.end}`, {
					Frozen: undefined,
					Chilled: undefined,
					Ambient: undefined,
				}
			]);
			return {
				...rCard,
				rangePrices: Object.fromEntries(entries)
			};
		});
		const update = {
			...rateCard,
			rate_cards: updatedRateCards
		}
		setRateCard(update as ILane);
		// @ts-ignore
		if (rcFormErrors?.rate_card_prices?.[rateCardTypeIndex]?.rangePrices) {
			clearRCSettingsError(`rate_card_prices.${rateCardTypeIndex}.rangePrices`)
		}
	}

	const handleEditRange = (previousRange: string, newRange: {
		start: string;
		end: string;
	}) => {
		if (isEditDisabled) return;

		const updatedRateCards = rateCard?.rate_cards?.map((rCard) => {
			if (rCard.type === 'FULL_LOAD') return rCard;
			if (!matchesRateCard(rCard, rc.type, rc.item_type_id)) return rCard;
			// Maintain existing order
			const updatedObj = Object.fromEntries(
				Object.entries(rCard.rangePrices).map(([key, value]) =>
					key === previousRange ? [`${newRange.start}-${newRange.end}`, value] : [key, value]
				)
			);

			return {
				...rCard,
				rangePrices: updatedObj
			};
		});
		const update = {
			...rateCard,
			rate_cards: updatedRateCards
		}

		setRateCard(update as ILane);
		// @ts-ignore
		if (rcFormErrors?.rate_card_prices?.[rateCardTypeIndex]?.rangePrices) {
			clearRCSettingsError(`rate_card_prices.${rateCardTypeIndex}.rangePrices`)
		}
	}

	const handleDeleteRange = (range: string) => {
		if (isEditDisabled) return;
		const updatedRateCards = rateCard?.rate_cards?.map((rCard) => {
			if (rCard.type === 'FULL_LOAD') return rCard;
			if (!matchesRateCard(rCard, rc.type, rc.item_type_id)) return rCard;
			const { [range as keyof ILaneRateCard['rangePrices']]: rangeToDelete, ...newRangePrices } = rCard.rangePrices;
			return {
				...rCard,
				rangePrices: newRangePrices
			}
		});
		const update = {
			...rateCard,
			rate_cards: updatedRateCards
		}
		setRateCard(update as ILane);
	}

	const getRateCardHeaderLabel = (value?: RateCardType) => {
		switch (value) {
		case 'PALLETS':
			return rateCard?.use_pallet_quantity_for_pricing ? 'Pallets' : 'Spaces';
		case 'WEIGHT':
			return 'Kilograms';
		case 'CPK':
			return 'Kilograms';
		case 'FULL_LOAD':
			return 'Truck';
		case 'ITEM':
			return itemType ? formatItemTypeUnits(itemType) : 'Units';
		default:
			break;
		}
	}

	const generateTableHeaders = () => {
		const headers = [<th key={'temp'}>Temperature</th>];
		if (rc?.type === 'FULL_LOAD') {
			sortedTruckList?.forEach((truck, index) => {
				headers.push(
					<th key={index}>
						<div className={styles.tableHeader}>
							<span>
								{getRateCardTruckSizeLabel(truck)}
							</span>
							{
								!isEditDisabled &&
								<div style={{ display: 'flex', gap: '8px' }}>
									<TruckSizeFloatingMenu
										rateCard={rc}
										trucks={TruckSizeOrderedBySize as RATE_CARD_TRUCK_SIZE[]}
										currentTruck={truck}
										isEdit
										onEdit={handleEditTruck}
										onDelete={handleDeleteTruck}
										isLastColumn={index === sortedTruckList.length -1}
									/>
									{
										index === sortedTruckList.length - 1 &&
										<TruckSizeFloatingMenu
											rateCard={rc}
											trucks={TruckSizeOrderedBySize as RATE_CARD_TRUCK_SIZE[]}
											currentTruck={truck}
											isEdit={false}
											onAdd={handleAddTruck}
											isLastColumn
										/>
									}
								</div>
							}
						</div>
					</th>
				)
			})
			if (!sortedTruckList?.length) {
				headers.push(
					<th key={'no-truck'}>
						<div className={`${styles.tableHeader} ${styles.tableHeaderCenter}`}>
							{
								!isEditDisabled &&
									<TruckSizeFloatingMenu
										rateCard={rc}
										trucks={TruckSizeOrderedBySize as RATE_CARD_TRUCK_SIZE[]}
										currentTruck={null}
										isEdit={false}
										onAdd={handleAddTruck}
									/>
							}
						</div>
					</th>
				)
			}
		} else {
			Object.keys(rc?.rangePrices || {}).forEach((range, index) => {
				headers.push(<th key={index}>
					<div className={styles.tableHeader}>
						<span>
							{range} {getRateCardHeaderLabel(rc?.type)}
						</span>
						{
							!isEditDisabled &&
									<div style={{ display: 'flex', gap: '8px' }}>
										<RangeFloatingMenu
											rateCard={rc}
											currentRange={range}
											isEdit
											onEdit={handleEditRange}
											onDelete={handleDeleteRange}
											index={index}
											isLastColumn={index === Object.keys(rc?.rangePrices || {}).length - 1}
										/>
										<RangeFloatingMenu
											rateCard={rc}
											isEdit={false}
											onAdd={handleAddRange}
											index={index}
											isLastColumn={index === Object.keys(rc?.rangePrices || {}).length - 1}
											currentRange=''
										/>
									</div>
						}
					</div>
				</th>
				)
			})

			if (rc && !Object.keys(rc.rangePrices).length) {
				headers.push(<th key={'edit'}>
					<div className={styles.tableHeader}>
						{
							!isEditDisabled &&
								<RangeFloatingMenu
									rateCard={rc}
									isEdit={false}
									onAdd={handleAddRange}
									index={0}
									currentRange=''
								/>
						}
					</div>
				</th>)
			}
		}
		if (settingType === 'Shipper' || settingType === 'Admin') {
			headers.push(<th key={'margin'} className={styles.marginCell}>
				<div className={styles.tableHeader}>MP margin</div>
			</th>);
		}
		return headers;
	}

	const generateTableRows = () => {
		const rows: any = [];
		const temps = [TEMP_NAMES.AMBIENT, TEMP_NAMES.CHILLED, TEMP_NAMES.FROZEN];

		temps.forEach((temp, index) => {
			const cols = [];
			if (rc && rc?.type !== 'FULL_LOAD') {
				for (const [key, range] of Object.entries(rc.rangePrices)) {
					cols.push(<td key={key}>
						<MemoizedNumberField
							name={`${rc?.type}-${key}`}
							onChange={(e) => handleInputChange(rc.type, temp, key, e.target.value)}
							value={typeof range[temp] === 'number' ? `${range[temp]}` : ''}
							disabled={isEditDisabled}
							leftModule={<span className={`${styles.prefix} ${!range[temp] && styles.prefixPlaceholder}` }>$</span>}
							min={'0'}
						/>
					</td>)
				}
				if (!Object.keys(rc.rangePrices).length) {
					cols.push(<td key={'empty'}></td>)
				}
			} else {
				sortedTruckList?.forEach((truck, index) => {
					if (rc?.type !== 'FULL_LOAD') return;
					cols.push(
						<td key={index}>
							<MemoizedNumberField
								value={typeof rc?.rangePrices?.[truck]?.[temp] === 'number' ? `${rc?.rangePrices?.[truck]?.[temp]}` : ''}
								onChange={(e) => handleInputChange(rc.type, temp, truck, e.target.value)}
								name={`${rc.type}-${truck}`}
								disabled={isEditDisabled}
								leftModule={<span className={`${styles.prefix} ${!rc?.rangePrices?.[truck]?.[temp] && styles.prefixPlaceholder}` }>$</span>}
								min={'0'}
							/>
						</td>
					)
				})
				if (!sortedTruckList?.length) {
					cols.push(<td key={'no-truck'}></td>)
				}
			}
			rows.push(<tr key={`row-${index}`}>
				<td key={'temp-td'}><TempChip name={temp as TEMP_NAMES} /></td>
				{...cols}
				{
					(settingType === 'Shipper' || settingType === 'Admin') ?
						<td key={'edit-margin-td'} className={styles.marginCell}>
							<MemoizedNumberField
								value={`${rc?.margins[temp]}` || '22'}
								onChange={(e) => handleInputChange(rc.type, temp, 'margin', e.target.value)}
								name={`${rc?.type}-margin`}
								rightModule={<span className={`${styles.suffix} ${!`${rc?.margins[temp]}` && styles.suffixPlaceholder}` }>%</span>}
								disabled={isRateCardRuleDisabled()}
								min={'0'}
							/>
						</td> : <></>
				}
			</tr>)
		})
		return rows;
	}

	const handleReportingInputChange = (rateCardType: RateCardType, name: string, value: string | null | undefined) => {
		const updatedRateCards = rateCard?.rate_cards?.map((rCard) => {
			if (!matchesRateCard(rCard, rateCardType, rc.item_type_id)) return rCard;
			return {
				...rCard,
				extra_attributes: {
					...rCard.extra_attributes,
					[name]: value
				}
			};
		});
		const update = {
			...rateCard,
			rate_cards: updatedRateCards
		}

		setRateCard(update as ILane);
		// @ts-ignore
		if (rcFormErrors?.rate_card_prices?.[rateCardTypeIndex]?.extra_attributes?.[name]) {
			clearRCSettingsError(`rate_card_prices.${rateCardTypeIndex}.extra_attributes.${name}`)
		}
	}

	const handleOnBlur = (rateCardType: RateCardType, name: string, value: string | null | undefined) => {
		if (value?.startsWith('.')) {
			handleReportingInputChange(rateCardType, name, `0${value}`)
		}
	}

	const handleCheckboxClick = (rateCardType: RateCardType, name: 'priced_annualized_volume' | 'priced_margin') => {
		rateCard?.rate_cards?.forEach((rCard) => {
			if (!matchesRateCard(rCard, rateCardType, rc.item_type_id)) return rCard;
			if (rCard.extra_attributes[name] === null) {
				handleReportingInputChange(rateCardType, name, undefined);
				return null;
			}
			handleReportingInputChange(rateCardType, name, null);
			return null;
		})
	}

	const handlePalletCalculationChange = (value: 'Quantity' | 'Spaces') => {
		if (!rateCard) return;

		const update = {
			...rateCard,
			use_pallet_quantity_for_pricing: value === 'Quantity'
		};
		setRateCard(update);
		clearRCSettingsError(`use_pallet_quantity_for_pricing`);
	}

	const getRateCardTitle = (value?: RateCardType) => {
		switch (value) {
		case 'PALLETS':
			return 'Pallets';
		case 'WEIGHT':
			return 'Tonnes';
		case 'CPK':
			return 'CPK ';
		case 'FULL_LOAD':
			return 'Full Loads';
		case 'ITEM':
			return getItemRateCardTitle(itemType, itemTypesLoaded);
		default:
			break;
		}
	}

	const getRateCardSubHeader = (value?: RateCardType) => {
		switch (value) {
		case 'PALLETS':
			return 'Pallets';
		case 'WEIGHT':
			return 'Tonnes';
		case 'CPK':
			return 'CPK';
		case 'FULL_LOAD':
			return 'Full Loads';
		default:
			break;
		}
	}

	const rateCardSubHeader = getRateCardSubHeader(rc?.type);

	const hasRangeError = () => {
		// @ts-ignore
		return rcFormErrors?.rate_card_prices?.[rateCardTypeIndex]?.rangePrices?._errors?.[0] || '';
	}

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.rate_card_prices?.[rateCardTypeIndex]?.extra_attributes?.[key]?._errors?.[0] || '';
	}

	const handleMinChargeChange = (e:ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		const isNumber = !isNaN(parseFloat(value));
		const realVal = isNumber ? parseFloat(parseFloat(value).toFixed(2)) : null;

		const update = {
			...rateCardRules.rateCard,
			min_charge: realVal
		} as TRateCardRules;

		const newRules = {
			settings: undefined,
			rateCard: update
		}

		setRateCardRules(newRules);
	}

	useEffect(() => {
		const fullLoadRateCard = rateCard?.rate_cards.find((rCard) => rCard.type === 'FULL_LOAD');
		const truckList = fullLoadRateCard?.rangePrices ? [...Object.keys(fullLoadRateCard.rangePrices)] : [];
		const sortedTruckSizesList = truckList;
		setSortedTruckList(sortedTruckSizesList as RATE_CARD_TRUCK_SIZE[])
	}, [rateCard?.rate_cards]);

	return (
		<div className={styles.rateCardContentContainer}>
			<div className={styles.rateCardTableHeader}>
				<div>
					<H3>
						{getRateCardTitle(rc?.type)}
					</H3>
					{rateCardSubHeader && <Text>{rateCardSubHeader}</Text>}
				</div>
				{/* TODO check permissions and disable */}
				{
					!quoteInRoute &&
					<Button
						Icon={TrashIcon}
						onClick={() => handleDeleteRateCard(rc.item_type_id ?? rc.type)}
						text={'Delete'}
						type={BUTTON_TYPES.TERTIARY}
						iconActiveColor='var(--of-primary)'
						disabled={isEditDisabled}
						iconPos={BUTTON_ICON_POS.LEFT}
					/>
				}
			</div>
			{
				itemType && <>
					<Spacer size={8} />
					<ItemTypeBanner itemType={itemType} inNewTab />
				</>
			}
			<div className={styles.rateCardTableContainer}>
				<Spacer size={20}/>
				<table className={styles.rateCardTable}>
					<thead>
						<tr>
							{generateTableHeaders()}
						</tr>
					</thead>
					<tbody>
						{generateTableRows()}
					</tbody>
				</table>
				<Spacer size={10}/>
			</div>
			<ErrorLabel
				noErrorNoContainer
				hasError={!!hasRangeError()}
				errorMessage={hasRangeError()}
			/>
			<Spacer size={20} />
			<Grid columns={2} gap='20px'>
				<Grid.Item>
					{
						settingType !== 'Carrier' && <>
							<Tile stretch>
								<H2>
								Reporting
								</H2>
								<Spacer size={16} />
								<Grid columns={2}>
									<Grid.Item>
										<InputField
											value={`${rc?.extra_attributes.priced_annualized_volume ?? ''}`}
											onChange={(e) => handleReportingInputChange(rc.type, 'priced_annualized_volume', e.target.value)}
											name={`${rc?.type}-priced_annualized_volume`}
											keepErrorSpace={false}
											placeholder='0.00'
											label='Priced annualised vol.'
											required
											hasError={!!hasError('priced_annualized_volume')}
											errorMessage={hasError('priced_annualized_volume')}
											disabled={rc?.extra_attributes.priced_annualized_volume === null || isRateCardRuleDisabled()}
											onBlur={(e) => handleOnBlur(rc.type, 'priced_annualized_volume', e.target.value)}
										/>
										<Spacer size={5} />
										<Checkbox
											name={'priced_annualized_volume_checkbox'}
											label={'Do not know volume yet'}
											value={''}
											onChange={() => handleCheckboxClick(rc.type, 'priced_annualized_volume')}
											disabled={isRateCardRuleDisabled()}
											checked={rc.extra_attributes.priced_annualized_volume === null}
										/>
									</Grid.Item>
									<Grid.Item>
										<NumberField
											value={`${rc?.extra_attributes.priced_margin ?? ''}`}
											onChange={(e) => handleReportingInputChange(rc.type, 'priced_margin', e.target.value)}
											name={`${rc?.type}-priced_margin`}
											keepErrorSpace={false}
											placeholder='0.00'
											leftModule={<span className={`${styles.prefix} ${!rc?.extra_attributes.priced_margin && styles.prefixPlaceholder}`}>%</span>}
											label='Priced Margin %'
											required
											hasError={!!hasError('priced_margin')}
											errorMessage={hasError('priced_margin')}
											disabled={rc?.extra_attributes.priced_margin === null || isRateCardRuleDisabled()}
											onBlur={(e) => handleOnBlur(rc.type, 'priced_margin', e.target.value)}
										/>
										<Spacer size={5} />
										<Checkbox
											name={'priced_margin_checkbox'}
											label={'Do not know margin yet'}
											value={''}
											onChange={() => handleCheckboxClick(rc.type, 'priced_margin')}
											disabled={isRateCardRuleDisabled()}
											checked={rc.extra_attributes.priced_margin === null}
										/>
									</Grid.Item>
								</Grid>
							</Tile>
							<Spacer size={20} />
						</>
					}
					<Tile stretch>
						<H3>Modifiers</H3>
						<Spacer size={20} />
						<Spacer divider size={0} />
						<RateCardModifiers settingType={settingType} type={rc.type} isDisabled={isEditDisabled} itemTypeId={rc.item_type_id} />
					</Tile>
					<Spacer size={20} />
					<Tile stretch>
						<CPIIncreasePanel
							cpiIncrease={rateCard?.latest_cpi_increase}
							isDisabled={isEditDisabled}
						/>
					</Tile>
				</Grid.Item>
				<Grid.Item>
					<Tile stretch>
						<H2>
							Configuration
						</H2>
						<Spacer size={16} />
						{
							rc.type === 'CPK' ?
								<CPKSettings isDisabled={isCPKConfigurationDisabled()} /> :
								<Grid columns={2}>
									{
										rc.type === 'PALLETS' &&
													<div>
														<Label required>Calculate ranges by:</Label>
														<Spacer size={8} />
														<Radio.Group>
															<Radio
																name={'use_pallet_quantity_for_pricing'}
																label={'Pallet spaces'}
																value={'Spaces'}
																onChange={() => handlePalletCalculationChange('Spaces')}
																checked={!rateCard?.use_pallet_quantity_for_pricing}
																disabled={isEditDisabled}
																keepErrorSpace={false}
															/>
															<Spacer size={8} />
															<Radio
																name={'use_pallet_quantity_for_pricing'}
																label={'Pallet quantity'}
																value={'Spaces'}
																onChange={() => handlePalletCalculationChange('Quantity')}
																checked={!!rateCard?.use_pallet_quantity_for_pricing}
																disabled={isEditDisabled}
																keepErrorSpace={false}
															/>
														</Radio.Group>
														{
															// @ts-ignore
															rcFormErrors?.use_pallet_quantity_for_pricing?._errors?.[0] &&
															<ErrorLabel
																hasError
																// @ts-ignore
																errorMessage={rcFormErrors?.use_pallet_quantity_for_pricing?._errors?.[0] || ''}
															/>
														}
													</div>
									}
									<NumberField
										label={'Min. Charge'}
										name={'min_charge'}
										value={`${rateCardRules?.rateCard?.min_charge ?? '0'}`}
										onChange={handleMinChargeChange}
										keepErrorSpace={false}
										placeholder={'0.00'}
										leftModule={<span className={`${styles.prefix} ${!rateCardRules?.rateCard?.min_charge && styles.prefixPlaceholder}`}>$</span>}
										disabled={isRateCardRuleDisabled()}
										required
									/>
								</Grid>
						}
					</Tile>
					<Spacer size={20} />
					{
						settingType !== 'Carrier' &&
						<>
							<Tile stretch>
								<H3>Rules</H3>
								<Spacer size={20} />
								<Spacer divider size={0} />
								<RateCardRules isDisabled={isRateCardRuleDisabled()} type={rc.type} itemTypeId={rc.item_type_id} />
							</Tile>
						</>
					}
				</Grid.Item>
			</Grid>
		</div>
	)
}